import { DocumentAddIcon } from "@heroicons/react/outline";
import { useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { DatasetEditorContext } from "@/components/DatasetEditor";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useMultipartUpload, EntityType } from "@/queries";
import { useOrganizationSubscriptionData } from "@/providers";
import { displayErrorToast } from "@/utils/toast";
import { useRbac } from "@/providers/RbacProvider";
import { BasicTooltip } from "@/components/ui/Tooltip";

const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(0)} GB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
};

const UploadDataButton = () => {
  const { isEditDisabled } = useRbac();
  const cannotEditDataset = isEditDisabled(EntityType.DATASET);

  const { datasetId } = useParams();
  const authContext = useAuth();
  const userContext = useUser();
  const { subscription } = useOrganizationSubscriptionData();
  const context = useContext(DatasetEditorContext);
  const workspaceId = userContext!.activeWorkspaceId;
  const { userToken } = authContext!;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const multipartFileUpload = useMultipartUpload(userToken!, workspaceId!);

  const startUpload = async (fileName: string) => {
    const response = await multipartFileUpload.mutateAsync({
      fileName: fileName,
      datasetId: parseInt(datasetId!, 10),
    });
    const { success, uploadId } = await response;
    if (!success) {
      throw new Error("File uploading failed");
    }
    return uploadId;
  };

  const uploadPart = async (uploadId: string, partNumber: number, part: Blob, fileName: string) => {
    const reader = new FileReader();
    reader.readAsDataURL(part);
    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        if (reader.result === null) {
          return;
        }
        try {
          const [, base64] = (reader.result as string).split(",");
          const response = await multipartFileUpload.mutateAsync({
            uploadId,
            fileName,
            partNumber,
            part: base64,
            datasetId: parseInt(datasetId!, 10),
          });
          const { success, ETag } = await response;
          if (!success) {
            reject("File uploading failed");
          }
          resolve(ETag);
        } catch {
          reject("File uploading failed");
        }
      };
      reader.onerror = error => reject(error);
    });
  };

  const completeUpload = async (
    fileName: string,
    uploadId: string,
    parts: { ETag: unknown; PartNumber: number }[],
  ) => {
    const response = await multipartFileUpload.mutateAsync({
      uploadId,
      fileName,
      parts,
      datasetId: parseInt(datasetId!, 10),
    });
    const { success } = await response;
    if (!success) {
      throw new Error("File uploading failed");
    }
  };

  const uploadFile = async (file: File) => {
    const partSize = 5 * 1024 * 1024; // 5MB
    const totalParts = Math.ceil(file.size / partSize);

    const fileExtension = file.name.split(".").pop();
    const uuidFileName = `${uuidv4()}.${fileExtension}`;

    try {
      context?.setIsUploadingToS3(true);
      const uploadId: string = await startUpload(uuidFileName);

      const uploadTasks = [];
      for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const part = file.slice(start, end);

        uploadTasks.push({
          partNumber,
          part,
          upload: () => uploadPart(uploadId, partNumber, part, uuidFileName),
        });
      }

      const maxConcurrent = import.meta.env.REACT_APP_MAX_CONCURRENT_UPLOADS || 3;
      const parts = [];

      for (let i = 0; i < uploadTasks.length; i += maxConcurrent) {
        const batch = uploadTasks.slice(i, i + maxConcurrent);

        const batchPromises = batch.map(task =>
          (async () => {
            const ETag = await task.upload();
            return { ETag, PartNumber: task.partNumber };
          })(),
        );

        const batchResults = await Promise.all(batchPromises);
        parts.push(...batchResults);

        const completedParts = Math.min(i + batch.length, totalParts);
        const progress = Math.round((completedParts / totalParts) * 70);
        context?.setProcessingProgress(progress);
      }

      context?.setProcessingProgress(70);
      await completeUpload(uuidFileName, uploadId, parts);
      context?.setIsUploadingToS3(false);
    } catch (error) {
      context?.setIsUploadingToS3(false);
      throw error;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const maxDatasetSize = subscription?.max_dataset_size;
    if (maxDatasetSize && file?.size > maxDatasetSize) {
      displayErrorToast(`File size must not exceed ${formatBytes(maxDatasetSize)}.`);
      return;
    }

    if (file && (file.type === "application/json" || file.type === "text/csv")) {
      context?.setProcessingType("upload");
      context?.setProcessingProgress(0);

      if (file.type === "application/json") {
        try {
          const fileContent = await file.text();
          const jsonContent = JSON.parse(fileContent);

          if (
            !Array.isArray(jsonContent) ||
            jsonContent.length === 0 ||
            typeof jsonContent[0] !== "object"
          ) {
            displayErrorToast("JSON file must contain an array of objects.");
            context?.setProcessingType(null);
            return;
          }
        } catch {
          displayErrorToast("Invalid JSON file.");
          context?.setProcessingType(null);
          return;
        }
      }

      try {
        await uploadFile(file);
      } catch {
        context?.setProcessingType(null);
        context?.setProcessingProgress(0);
      }
    } else {
      displayErrorToast("Only .json and .csv files are accepted.");
    }
  };

  const handleImportDataClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click(); // eslint-disable-next-line no-empty
    } else {
    }
  };

  if (!context?.isDraft) {
    return null;
  }

  return (
    <BasicTooltip
      content={
        cannotEditDataset ? "You don't have permission to upload data to this dataset" : undefined
      }
      shouldShow={cannotEditDataset}
    >
      <span>
        <Button
          disabled={
            cannotEditDataset ||
            multipartFileUpload.isLoading ||
            context?.processingType === "upload" ||
            context?.processingType === "deleteRows"
          }
          onClick={handleImportDataClick}
          size="sm"
          variant="outline"
        >
          <DocumentAddIcon aria-hidden="true" className="h-4 w-auto pr-1" />
          Upload data
        </Button>
        <input
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
          type="file"
        />
      </span>
    </BasicTooltip>
  );
};

export default UploadDataButton;
